import { initAnalyticsListener } from "../../packages/analytics";
export {
	default,
	useAnalyticsQueryParam,
	useAnalytics,
	withAnalytics,
	AnalyticsProvider,
} from "../../packages/analytics";

window.LEXUS = window.LEXUS || {};
window.LEXUS.ANALYTICS_DEFINITIONS = window.LEXUS.ANALYTICS_DEFINITIONS || {};

if (window.LEXUS.ANALYTICS_DEFINITIONS._state !== "LOADED") {
	console.log("[Analytics Hook] DEFINITIONS LOADED");

	initAnalyticsListener((e) => {
		const { eventName, data } = e.detail;
		console.log("[Analytics Hook]", eventName, data);
		if (window.LEXUS.features?.ANALYTICS_QUEUEING === true) {
			window.analyticsHooks = window.analyticsHooks || [];
			window.analyticsHooks.push({
				event: eventName,
				data: data,
			});
		} else {
			resolveAnalytics(window.LEXUS.ANALYTICS_DEFINITIONS, eventName, data);
		}
	});
	window.LEXUS.ANALYTICS_DEFINITIONS._state = "LOADED";
} else {
	console.log("[Analytics Hook] DEFINITIONS ALREADY LOADED");
}

/***********************************************
 *  Analytics Definition Resolver
 ***********************************************/
const resolveAnalytics = function (definitions, eventName, data = {}) {
	const { _tree = [] } = data;
	// convert _tree to dictionary for easy state access
	const stateDict = _tree.reduce((data, item) => {
		data[item._component] = item;
		return data;
	}, {});
	const eventData = {
		event: data,
		states: stateDict,
	};
	const [, action] = eventName.split("/");
	// create extractor for component and componentID and inject them to the st
	const getComponentName = (item) => item._component;
	const getComponentID = (item) => item._props.id || "";
	const maxDepth = 7;
	const stateList = getAnalyticsStates(
		_tree,
		action,
		getComponentName,
		getComponentID,
		maxDepth,
	);
	const formatters = stateList.reduce((list, state) => {
		const formatter = definitions[state];
		let stateData;
		if (formatter) {
			try {
				stateData = formatter({ ...eventData });
				list.push({
					state: state,
					data: stateData,
				});
			} catch (e) {
				console.log("error in state (" + state + ") collector", e.message);
			}
		}
		return list;
	}, []);
	const analyticsData = formatters.reduce((merged, stateData) => {
		const { data } = stateData;
		return {
			...merged,
			...data,
		};
	}, {});
	if (analyticsData.event && analyticsData.event !== "ignore") {
		// Push to queue
		window.__analytics_queue__ = window.__analytics_queue__ || [];
		window.__analytics_queue__.push({
			event: analyticsData.event,
			data: analyticsData,
		});
	}
};

/**
 * This function walks the array of component states and builds a heirarchal list of possible
 * analytics definitions. Extractor functioncan be used to adapt to different data structures
 *
 * Limitations: does not handle nesting components of same type within each other
 *
 * @param {Array} tree  an list of component states
 * @param {String} action current event
 * @param {Function} getComponentName an extractor function to return component name
 * @param {Function} getComponentID an extractor function to return component ID
 * @param {Integer}  maxDepth an integer limiting how far up the tree to resolve
 */
function getAnalyticsStates(
	tree,
	action,
	getComponentName = (item) => item._component,
	getComponentID = () => "",
	maxDepth = -1,
) {
	const len = tree.length;
	const simplifiedTree = tree.map((item, index) => {
		const _component = getComponentName(item);
		const id = getComponentID(item);
		return {
			index: len - index,
			_component,
			id,
		};
	});
	const prunedTree =
		maxDepth > 0 ? simplifiedTree.slice(-maxDepth) : simplifiedTree;
	const states = prunedTree.reduce((stateArray, treeItem) => {
		const { _component, index, id } = treeItem;
		const item = {
			_component,
			weight: index,
		};
		const prev = stateArray.slice(-1)[0];
		const weightAction = 0.5;
		const weightID = 1000;
		let itemId;
		let itemAction;
		let itemIdAction;
		const currentStates = [item];
		if (index === 1) {
			itemAction = {
				_component: _component + "/" + action,
				weight: index + 1 + weightAction,
			};
			currentStates.push(itemAction);
		}
		if (id) {
			itemId = {
				_component: _component + "#" + id,
				weight: index + 1 + weightID,
			};
			currentStates.push(itemId);

			if (index === 1) {
				itemIdAction = {
					_component: itemId._component + "/" + action,
					weight: itemId.index + weightAction,
				};
				currentStates.push(itemIdAction);
			}
		}

		if (prev && prev.length > 0) {
			for (var i = 0; i < prev.length; i++) {
				var prevComp = prev[i]._component;
				var prevWeight = prev[i].weight;

				currentStates.push({
					_component: prevComp + ":" + item._component,
					weight: item.weight + prevWeight,
				});
				// check ID version
				if (itemId) {
					currentStates.push({
						_component: prevComp + ":" + itemId._component,
						weight: itemId.weight + prevWeight,
					});
				}
				if (itemAction) {
					currentStates.push({
						_component: prevComp + ":" + itemAction._component,
						weight: itemAction.weight + prevWeight,
					});
				}
				if (itemIdAction) {
					currentStates.push({
						_component: prevComp + ":" + itemIdAction._component,
						weight: itemIdAction.weight + prevWeight,
					});
				}
			}
		}
		stateArray.push(currentStates);
		return stateArray;
	}, []);
	const flattened = Array.prototype.concat.apply([], states);
	return flattened
		.sort(function (a, b) {
			if (a.weight > b.weight) {
				return 1;
			}
			if (a.weight < b.weight) {
				return -1;
			}
			return 0;
		})
		.map((item) => item._component);
}
